import React, { useState } from 'react';
import { NodeProperty, PropertyValue } from '@shared/types/node-properties';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { CodeMirrorEditor, EditorLanguage } from '@/components/workflow/node-editor/CodeMirrorEditor';
import { DroppableInput } from '@/components/workflow/node-editor/DroppableInput';
import { DroppableTextarea } from '@/components/workflow/node-editor/DroppableTextarea';
import { toast } from 'sonner';
import { webhooksApi } from '@/lib/api';
import {
  CollectionPropertyRenderer,
  FixedCollectionPropertyRenderer,
  OptionsPropertyRenderer,
  MultiOptionsPropertyRenderer,
  NoticePropertyRenderer,
  JsonSchemaEditorRenderer,
} from './renderers';

interface EnhancedPropertyEditorProps {
  property: NodeProperty;
  value: PropertyValue;
  onChange: (value: PropertyValue) => void;
  error?: string;
  disabled?: boolean;
  hideLabel?: boolean; // Hide label when used inside fixedCollection
  nodeId?: string; // Optional node ID for dynamic content
  allValues?: Record<string, any>; // All property values for context
  onPropertyChange?: (key: string, value: any) => void; // Callback to update any property
}

export const EnhancedPropertyEditor: React.FC<EnhancedPropertyEditorProps> = ({
  property,
  value,
  onChange,
  error,
  disabled = false,
  hideLabel = false,
  nodeId,
  allValues = {},
  onPropertyChange,
}) => {
  const [isTextareaExpanded, setIsTextareaExpanded] = useState(false);
  
  const renderEditor = () => {
    switch (property.type) {
      // New enhanced types
      case 'collection':
        return (
          <CollectionPropertyRenderer
            property={property}
            value={value as Record<string, any>[]}
            onChange={onChange}
            disabled={disabled}
            error={error}
          />
        );

      case 'fixedCollection':
        return (
          <FixedCollectionPropertyRenderer
            property={property}
            value={value as Record<string, Record<string, any>[]>}
            onChange={onChange}
            disabled={disabled}
            error={error}
          />
        );

      case 'options':
        return (
          <OptionsPropertyRenderer
            property={property}
            value={value as string | number}
            onChange={onChange}
            disabled={disabled}
            error={error}
            hideLabel={hideLabel}
          />
        );

      case 'multiOptions':
        return (
          <MultiOptionsPropertyRenderer
            property={property}
            value={value as (string | number)[]}
            onChange={onChange}
            disabled={disabled}
            error={error}
            hideLabel={hideLabel}
          />
        );

      case 'notice':
        return <NoticePropertyRenderer property={property} nodeId={nodeId} values={allValues} />;

      case 'jsonSchemaEditor':
        // Enhanced JSON Schema editor with validation, preview, templates, and testing
        // Value is expected to be string (JSON text) or object (parsed JSON)
        const schemaEditorValue = typeof value === 'string' || typeof value === 'object' ? value : String(value ?? '');
        return (
          <JsonSchemaEditorRenderer
            property={property}
            value={schemaEditorValue as string | object}
            onChange={onChange}
            disabled={disabled}
            error={error}
            outputType={allValues?.expectedOutputType || 'either'}
            onOutputTypeChange={onPropertyChange ? (type) => onPropertyChange('expectedOutputType', type) : undefined}
            schemaDescription={allValues?.schemaDescription || ''}
            onSchemaDescriptionChange={onPropertyChange ? (desc) => onPropertyChange('schemaDescription', desc) : undefined}
          />
        );

      case 'hidden':
        return null;

      // Basic types
      case 'string':
        // If the property has rows defined, render as textarea with expand/collapse
        if (property.typeOptions?.rows) {
          const rows = property.typeOptions.rows;
          return (
            <div className="relative">
              <DroppableTextarea
                value={(value as string) || ''}
                onValueChange={onChange}
                placeholder={property.placeholder}
                disabled={disabled}
                rows={isTextareaExpanded ? 20 : rows}
                className={`${error ? 'border-destructive' : ''} ${isTextareaExpanded ? 'min-h-[500px]' : ''}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 h-6 w-6 p-0"
                onClick={() => setIsTextareaExpanded(!isTextareaExpanded)}
                disabled={disabled}
              >
                {isTextareaExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          );
        }
        // Otherwise render as single-line input
        return (
          <DroppableInput
            value={(value as string) || ''}
            onValueChange={onChange}
            placeholder={property.placeholder}
            disabled={disabled}
            className={error ? 'border-destructive' : ''}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={(value as number) || ''}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            placeholder={property.placeholder}
            disabled={disabled}
            min={property.typeOptions?.minValue}
            max={property.typeOptions?.maxValue}
            step={property.typeOptions?.numberStepSize || 1}
            className={error ? 'border-destructive' : ''}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={(value as boolean) || false}
              onCheckedChange={onChange}
              disabled={disabled}
            />
            <span className="text-sm text-muted-foreground">
              {(value as boolean) ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        );

      case 'json':
        const jsonValue = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
        const hasTemplateVariables = /\{\{[^}]+\}\}/.test(jsonValue);

        return (
          <div className="space-y-2">
            {hasTemplateVariables && (
              <Alert className="py-2">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Contains template variables - will be validated at runtime
                </AlertDescription>
              </Alert>
            )}
            <CodeMirrorEditor
              height="200px"
              language="json"
              value={jsonValue}
              onChange={(val) => {
                try {
                  onChange(JSON.parse(val || '{}'));
                } catch {
                  onChange(val || '{}');
                }
              }}
              readOnly={disabled}
            />
          </div>
        );

      case 'code':
        const codeLanguage = (property.typeOptions?.language || 'javascript') as EditorLanguage;
        return (
          <CodeMirrorEditor
            height="300px"
            language={codeLanguage}
            value={(value as string) || ''}
            onChange={(val) => onChange(val || '')}
            readOnly={disabled}
          />
        );

      case 'color':
        return (
          <div className="flex items-center space-x-2">
            <Input
              type="color"
              value={(value as string) || '#000000'}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              className="w-20 h-10"
            />
            <Input
              type="text"
              value={(value as string) || '#000000'}
              onChange={(e) => onChange(e.target.value)}
              placeholder="#000000"
              disabled={disabled}
              className={error ? 'border-destructive' : ''}
            />
          </div>
        );

      case 'dateTime':
        return (
          <Input
            type="datetime-local"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={error ? 'border-destructive' : ''}
          />
        );

      // Fallback for unknown types
      default:
        return (
          <Textarea
            value={typeof value === 'string' ? value : JSON.stringify(value)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={property.placeholder}
            disabled={disabled}
            rows={3}
            className={error ? 'border-destructive' : ''}
          />
        );
    }
  };

  // Don't render label/description for notice, hidden types, or when hideLabel is true
  if (property.type === 'notice' || property.type === 'hidden' || hideLabel) {
    return <>{renderEditor()}</>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Label htmlFor={property.key}>
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {property.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">{property.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {renderEditor()}

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {property.hint && !error && (
        <p className="text-xs text-muted-foreground">{property.hint}</p>
      )}
    </div>
  );
};
